/**
 * Verops RUM SDK
 * Real User Monitoring JavaScript Agent
 * @version 2.3.3
 * @license MIT
 *
 * v2.3.3 (2026-11-03)
 *   - the unload path sends size-bounded chunks within the browser's 64 KB
 *     keepalive budget (larger unload batches were rejected and lost)
 *   - the SDK no longer reports its own send failures as the site's
 *     console errors
 *
 * v2.3.2 (2026-11-03)
 *   - Safari is detected again (real Safari UAs put Version/ before Safari/)
 *   - web vitals are reported once, when the page is hidden, with final
 *     values; the old 5 s timer dropped late LCP, INP and CLS and sent
 *     nothing for pages left sooner
 *   - events queued when the page hides are always sent, even while an
 *     earlier send is still in flight
 *   - setUser('id') is accepted as shorthand for setUser({ id: 'id' })
 *   - the user agent is parsed once per page instead of once per event
 *
 * v2.2.0 (2026-05-01) — page-tracking fixes for SPAs
 *   - hash-routed apps (HashRouter, AngularJS, Vue hash mode) are now
 *     tracked: hashchange listener + page_path includes hash when it
 *     starts with "#/"
 *   - search params are part of page_path so /products?cat=a and
 *     /products?cat=b are separate rows
 *   - page_view fires after an 80 ms defer so the framework has time
 *     to update document.title before we capture it
 *   - same getLogicalPath() helper used by every event (page_view,
 *     click, error, web-vital), keeping correlation tight across
 *     signals on the dashboard
 */

(function(window) {
  'use strict';

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Generate a UUID v4
   */
  function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Get current timestamp in milliseconds
   */
  function now() {
    return Date.now();
  }

  /**
   * Get high-resolution timestamp
   */
  function getPerformanceNow() {
    return performance && performance.now ? performance.now() : now();
  }

  /**
   * Safely execute a function
   */
  function safelyExecute(fn, context) {
    try {
      return fn.call(context);
    } catch (e) {
      console.error('Verops RUM: Error executing function', e);
      return null;
    }
  }

  /**
   * Deep merge objects
   */
  function mergeObjects(target, source) {
    var output = Object.assign({}, target);
    if (isObject(target) && isObject(source)) {
      Object.keys(source).forEach(function(key) {
        if (isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = mergeObjects(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  }

  function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  /**
   * Throttle function execution
   */
  function throttle(fn, delay) {
    var lastCall = 0;
    return function() {
      var now = Date.now();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return fn.apply(this, arguments);
    };
  }

  /**
   * Debounce function execution
   */
  function debounce(fn, delay) {
    var timeoutId;
    return function() {
      var context = this;
      var args = arguments;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function() {
        fn.apply(context, args);
      }, delay);
    };
  }

  /**
   * Get page URL without sensitive data
   */
  function getSanitizedURL() {
    return window.location.href.split('?')[0];
  }

  /**
   * Get user agent
   */
  // Read once: the UA cannot change for a page, and re-reading it on every
  // event cost a regex pass per event.
  var cachedUserAgent = null;
  function getUserAgent() {
    if (cachedUserAgent === null) cachedUserAgent = navigator.userAgent || '';
    return cachedUserAgent;
  }

  /**
   * Parse the UA into browser/os/deviceType triple. Mirrors the
   * server-side UserAgentParser.java fallback used for legacy events;
   * having the client parse means the dedicated columns get populated
   * at ingest, so the backend doesn't have to re-parse on every read.
   *
   * Returns null fields rather than 'unknown' so the gateway can still
   * apply its own DEFAULT 'unknown' if neither end produces a value.
   */
  var cachedFingerprint = null;
  function detectClientFingerprint() {
    if (cachedFingerprint) return cachedFingerprint;
    var ua = getUserAgent();
    var browser = null, os = null, deviceType = null;

    // Browser — order matters (Edge/Opera embed "Chrome").
    if (/Edg\/|Edge\/|EdgA\/|EdgiOS\//.test(ua)) browser = 'Edge';
    else if (/OPR\/|Opera\/|OPiOS\//.test(ua))   browser = 'Opera';
    else if (/Brave\//.test(ua))                  browser = 'Brave';
    else if (/SamsungBrowser\//.test(ua))         browser = 'Samsung Internet';
    else if (/UCBrowser\//.test(ua))              browser = 'UC Browser';
    else if (/Firefox\/|FxiOS\//.test(ua))        browser = 'Firefox';
    else if (/CriOS\//.test(ua))                  browser = 'Chrome';
    else if (/Chrome\/|Chromium\//.test(ua))      browser = 'Chrome';
    // Real Safari UAs put Version/x before Safari/y ("Version/17.4 Mobile/15E148 Safari/604.1").
    else if (/Version\/[\d.]+.*Safari\//.test(ua)) browser = 'Safari';
    else if (/MSIE |Trident\//.test(ua))          browser = 'Internet Explorer';

    // OS — iOS first because iPhone/iPad UAs also mention "Mac OS X".
    if (/iPhone|iPad|iPod|\siOS\s/.test(ua))   os = 'iOS';
    else if (/Android/.test(ua))                os = 'Android';
    else if (/Windows NT|Win64|Win32/.test(ua)) os = 'Windows';
    else if (/Mac OS X|Macintosh/.test(ua))     os = 'macOS';
    else if (/CrOS/.test(ua))                   os = 'Chrome OS';
    else if (/Linux/.test(ua))                  os = 'Linux';
    else if (/FreeBSD/.test(ua))                os = 'FreeBSD';

    // Device — Tablet first (Android tablets DROP "Mobile" from UA).
    if (/iPad|PlayBook|Kindle|Tablet/.test(ua) ||
        (/Android/.test(ua) && !/Mobile/.test(ua))) deviceType = 'Tablet';
    else if (/Mobile|iPhone|iPod|Android|BlackBerry|Opera Mini|IEMobile/.test(ua)) deviceType = 'Mobile';
    else if (browser != null) deviceType = 'Desktop';

    cachedFingerprint = { browser: browser, os: os, device_type: deviceType };
    return cachedFingerprint;
  }

  /**
   * Get screen resolution
   */
  function getScreenResolution() {
    return {
      width: screen.width,
      height: screen.height,
      availWidth: screen.availWidth,
      availHeight: screen.availHeight,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth
    };
  }

  /**
   * Get viewport size
   */
  function getViewportSize() {
    return {
      width: window.innerWidth || document.documentElement.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight
    };
  }

  /**
   * Check if value should be sampled
   */
  function shouldSample(rate) {
    return Math.random() < rate;
  }

  /**
   * Extract UTM parameters from URL
   */
  function getUTMParameters() {
    try {
      var params = {};
      var urlParams = new URLSearchParams(window.location.search);
      var utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

      utmKeys.forEach(function(key) {
        var value = urlParams.get(key);
        if (value) {
          params[key] = value;
        }
      });

      return Object.keys(params).length > 0 ? params : null;
    } catch (e) {
      console.warn('Verops RUM: Error extracting UTM parameters', e);
      return null;
    }
  }

  /**
   * Check if event should be filtered (SDK internal events)
   */
  function shouldFilterSDKEvent(url) {
    if (!url) return false;

    // Don't track SDK's own beacon calls
    return url.indexOf('/api/v1.0/rum/beacon') !== -1 ||
           url.indexOf('rum-ingestion') !== -1 ||
           url.indexOf(':10350') !== -1;
  }

  // ============================================================================
  // SMART ACTION CLASSIFIER
  // ============================================================================

  var SmartActions = (function() {
    var patterns = {
      conversion: {
        keywords: /buy|purchase|checkout|subscribe|add.*cart|order|payment|register|signup/i,
        selectors: ['[data-action="checkout"]', '[data-action="purchase"]', '.checkout-btn', '.buy-btn', '.add-to-cart'],
        confidence: 0.90
      },
      navigation: {
        keywords: /menu|nav|tab|breadcrumb|back|forward|home/i,
        selectors: ['nav a', '[role="tab"]', '[role="navigation"]', '.nav-link'],
        confidence: 0.85
      },
      search: {
        keywords: /search|find|filter|query/i,
        selectors: ['[type="search"]', '[role="search"]', '.search-btn', '.filter-btn'],
        confidence: 0.90
      },
      social: {
        keywords: /share|like|follow|tweet|post|comment/i,
        selectors: ['[data-action="share"]', '.share-btn', '.social-btn'],
        confidence: 0.85
      }
    };

    function classify(element, event) {
      if (!element) return { actionType: 'unknown', confidence: 0 };

      var text = getElementText(element);
      var classes = (typeof element.className === 'string') ? element.className : '';
      var dataAction = element.getAttribute ? (element.getAttribute('data-action') || '') : '';
      var combined = (text + ' ' + classes + ' ' + dataAction).toLowerCase();

      // Check each pattern
      for (var type in patterns) {
        var pattern = patterns[type];
        var score = 0;

        // Keyword matching
        if (pattern.keywords.test(combined)) {
          score = pattern.confidence;

          return {
            actionType: type,
            confidence: score,
            element: getElementSelector(element),
            text: text
          };
        }

        // Selector matching (simplified)
        for (var i = 0; i < pattern.selectors.length; i++) {
          if (matchesSimpleSelector(element, pattern.selectors[i])) {
            return {
              actionType: type,
              confidence: pattern.confidence,
              element: getElementSelector(element),
              text: text
            };
          }
        }
      }

      return { actionType: 'unknown', confidence: 0 };
    }

    function matchesSimpleSelector(element, selector) {
      // Simple selector matching
      if (selector.startsWith('#')) {
        return element.id === selector.substring(1);
      }
      if (selector.startsWith('.')) {
        var className = selector.substring(1);
        return element.className && element.className.indexOf(className) !== -1;
      }
      if (selector.startsWith('[')) {
        // Attribute selector like [data-action="checkout"]
        var match = selector.match(/\[([^=]+)="([^"]+)"\]/);
        if (match) {
          return element.getAttribute(match[1]) === match[2];
        }
      }
      return false;
    }

    function getElementSelector(element) {
      if (!element || !element.tagName) return '';
      if (element.id) return '#' + element.id;

      var classes = '';
      if (element.className && typeof element.className === 'string') {
        classes = element.className.trim().split(/\s+/).slice(0, 2).join('.');
      }

      return element.tagName.toLowerCase() + (classes ? '.' + classes : '');
    }

    function getElementText(element) {
      if (!element) return '';

      // Check aria-label first
      if (element.getAttribute('aria-label')) {
        return element.getAttribute('aria-label');
      }

      // Check title
      if (element.getAttribute('title')) {
        return element.getAttribute('title');
      }

      // Get text content
      var text = element.textContent || element.innerText || '';
      return text.trim().substring(0, 100);
    }

    return {
      classify: classify,
      getElementSelector: getElementSelector
    };
  })();

  // ============================================================================
  // FRUSTRATION DETECTOR
  // ============================================================================

  var FrustrationDetector = (function() {
    var clickHistory = [];
    var enabled = false;

    function init() {
      enabled = true;
      document.addEventListener('click', detectRageClick, true);
    }

    function detectRageClick(event) {
      if (!enabled) return;

      var element = event.target;
      var now = Date.now();

      // Record click
      clickHistory.push({
        element: element,
        selector: SmartActions.getElementSelector(element),
        timestamp: now
      });

      // Keep only recent clicks (last 2 seconds)
      clickHistory = clickHistory.filter(function(c) {
        return now - c.timestamp < 2000;
      });

      // Count clicks on same element in last 1 second
      var recentClicks = clickHistory.filter(function(c) {
        return c.element === element && now - c.timestamp < 1000;
      });

      // Rage click detection: 3+ clicks in 1 second
      if (recentClicks.length >= 3) {
        // Avoid duplicate events
        var lastRageClick = clickHistory.lastRageClickTime || 0;
        if (now - lastRageClick < 2000) return;

        clickHistory.lastRageClickTime = now;

        if (window.VeropsRUM && window.VeropsRUM.trackEvent) {
          window.VeropsRUM.trackEvent('frustration_detected', {
            type: 'rage_click',
            element: SmartActions.getElementSelector(element),
            click_count: recentClicks.length,
            page: window.location.pathname,
            frustration_score: Math.min(recentClicks.length / 10, 1.0)
          });
        }
      }
    }

    return {
      init: init,
      destroy: function() {
        enabled = false;
        document.removeEventListener('click', detectRageClick, true);
      }
    };
  })();

  // ============================================================================
  // FORM TRACKER
  // ============================================================================

  var FormTracker = (function() {
    var formInteractions = {};
    var enabled = false;
    var trackedForms = [];

    function init() {
      enabled = true;
      trackForms();
    }

    function trackForms() {
      var forms = document.querySelectorAll('form');

      for (var i = 0; i < forms.length; i++) {
        var form = forms[i];

        // Skip if already tracked
        if (trackedForms.indexOf(form) !== -1) continue;
        trackedForms.push(form);

        var formId = form.id || form.name || 'form-' + i;

        // Track field interactions
        var inputs = form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]), textarea, select');
        for (var j = 0; j < inputs.length; j++) {
          setupFieldTracking(inputs[j], formId);
        }

        // Track form submission
        (function(formElement, id) {
          formElement.addEventListener('submit', function(e) {
            var formData = formInteractions[id] || {};
            var fieldCount = Object.keys(formData).length - 1; // Exclude startTime

            if (window.VeropsRUM && window.VeropsRUM.trackEvent) {
              window.VeropsRUM.trackEvent('form_submitted', {
                form_id: id,
                field_count: fieldCount,
                total_time: formData.startTime ? Date.now() - formData.startTime : 0
              });
            }
          });
        })(form, formId);
      }
    }

    function setupFieldTracking(field, formId) {
      var fieldName = field.name || field.id || field.type || 'field';

      // Track focus
      field.addEventListener('focus', function() {
        if (!formInteractions[formId]) {
          formInteractions[formId] = { startTime: Date.now() };
        }
        formInteractions[formId][fieldName] = {
          focusTime: Date.now()
        };
      });

      // Track blur (when user leaves field)
      field.addEventListener('blur', function() {
        if (formInteractions[formId] && formInteractions[formId][fieldName]) {
          var focusTime = formInteractions[formId][fieldName].focusTime;
          var duration = Date.now() - focusTime;

          if (window.VeropsRUM && window.VeropsRUM.trackEvent) {
            window.VeropsRUM.trackEvent('form_field_interaction', {
              form_id: formId,
              field_name: fieldName,
              field_type: field.type,
              duration_ms: duration
            });
          }
        }
      });
    }

    return {
      init: init,
      destroy: function() {
        enabled = false;
        formInteractions = {};
        trackedForms = [];
      }
    };
  })();

  // ============================================================================
  // SESSION MANAGER
  // ============================================================================

  var SessionManager = (function() {
    var SESSION_KEY = 'Verops_rum_session';
    var SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    var currentSession = null;

    function getStoredSession() {
      try {
        var stored = localStorage.getItem(SESSION_KEY);
        if (stored) {
          var session = JSON.parse(stored);
          var now = Date.now();
          if (now - session.lastActivity < SESSION_TIMEOUT) {
            return session;
          }
        }
      } catch (e) {
        console.warn('Verops RUM: Error reading session from storage', e);
      }
      return null;
    }

    function createNewSession() {
      return {
        id: generateUUID(),
        startTime: Date.now(),
        lastActivity: Date.now(),
        pageViews: 0,
        events: 0
      };
    }

    function saveSession(session) {
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      } catch (e) {
        console.warn('Verops RUM: Error saving session to storage', e);
      }
    }

    function getSession() {
      if (!currentSession) {
        currentSession = getStoredSession() || createNewSession();
      }
      return currentSession;
    }

    function updateActivity() {
      var session = getSession();
      session.lastActivity = Date.now();
      saveSession(session);
    }

    function incrementPageViews() {
      var session = getSession();
      session.pageViews++;
      saveSession(session);
    }

    function incrementEvents() {
      var session = getSession();
      session.events++;
      saveSession(session);
    }

    function getSessionId() {
      return getSession().id;
    }

    function endSession() {
      try {
        localStorage.removeItem(SESSION_KEY);
      } catch (e) {
        console.warn('Verops RUM: Error removing session from storage', e);
      }
      currentSession = null;
    }

    return {
      getSessionId: getSessionId,
      updateActivity: updateActivity,
      incrementPageViews: incrementPageViews,
      incrementEvents: incrementEvents,
      endSession: endSession
    };
  })();

  // ============================================================================
  // BEACON SENDER
  // ============================================================================

  var BeaconSender = (function() {
    var queue = [];
    var isSending = false;
    var maxQueueSize = 100;
    var flushInterval = 5000; // 5 seconds
    var retryAttempts = 3;
    var endpoint = '';
    var apiKey = '';

    function init(apiEndpoint, apiKeyValue) {
      endpoint = apiEndpoint;
      apiKey = apiKeyValue;

      // Start periodic flush
      setInterval(function() {
        flush();
      }, flushInterval);

      // Flush on page unload
      if ('onpagehide' in window) {
        window.addEventListener('pagehide', flushAll, false);
      } else {
        window.addEventListener('beforeunload', flushAll, false);
      }

      // Flush on visibility change
      document.addEventListener('visibilitychange', function() {
        if (document.visibilityState === 'hidden') {
          flushAll();
        }
      });
    }

    // Browsers cap the bodies of in-flight keepalive requests at 64 KB per
    // page and reject anything past that with "Failed to fetch", so the
    // unload path sends size-bounded chunks within that budget.
    var KEEPALIVE_BUDGET = 60000;
    var keepaliveInFlight = 0;

    // The page may be going away: send everything queued now, even while a
    // periodic send is still in flight (flush() would skip and lose it).
    // keepalive lets these requests outlive the page.
    function flushAll() {
      for (var i = 0; i < beforeFlushAll.length; i++) {
        try { beforeFlushAll[i](); } catch (e) { /* never block the flush */ }
      }
      while (queue.length > 0) {
        var chunk = [];
        var bytes = 2;
        while (queue.length > 0) {
          var size = JSON.stringify(queue[0]).length + 1;
          if (chunk.length > 0 && (chunk.length >= maxQueueSize || bytes + size > KEEPALIVE_BUDGET)) break;
          chunk.push(queue.shift());
          bytes += size;
        }
        send(chunk);
      }
    }

    var beforeFlushAll = [];

    function enqueue(event) {
      // Vitals are reported as the page goes away; put them first so they
      // fit in the keepalive budget ahead of bulkier queued events.
      if (event && event.event_type === 'web_vitals') queue.unshift(event);
      else queue.push(event);
      SessionManager.incrementEvents();

      if (queue.length >= maxQueueSize) {
        flush();
      }
    }

    function flush() {
      if (queue.length === 0 || isSending) {
        return;
      }

      var batch = queue.splice(0, maxQueueSize);
      send(batch);
    }

    function send(events, attempt) {
      attempt = attempt || 0;
      isSending = true;

      var payload = JSON.stringify(events);
      // Only use keepalive while the page's budget allows it; past the
      // budget a normal request still has a chance, a keepalive one has none.
      var keepalive = keepaliveInFlight + payload.length <= KEEPALIVE_BUDGET;
      if (keepalive) keepaliveInFlight += payload.length;
      var settle = function() { if (keepalive) keepaliveInFlight -= payload.length; };

      // Why not navigator.sendBeacon? sendBeacon hardwires credentials:'include'
      // (no opt-out), so the browser demands Access-Control-Allow-Credentials:true
      // on the preflight. The gateway deliberately does NOT enable that — we don't
      // want customer cookies leaving their site and arriving at ingest.verops.io.
      // sendBeacon also can't set custom headers, which would mean shipping the
      // API key in ?apiKey= — keys in URLs end up in proxy logs, browser history,
      // and Referer headers, and the gateway explicitly rejects URL-supplied keys.
      //
      // fetch with keepalive:true is the modern equivalent: survives page unload
      // (pagehide/beforeunload) like sendBeacon, but lets us set X-RUM-Key in a
      // header AND credentials:'omit'. Content-Type:text/plain because the
      // gateway parses bytes regardless of content-type and text/plain is
      // CORS-safelisted (smaller preflight surface).
      if (typeof fetch !== 'undefined') {
        var headers = { 'Content-Type': 'text/plain' };
        if (apiKey) {
          headers['X-RUM-Key'] = apiKey;
        }

        fetch(endpoint, {
          method: 'POST',
          headers: headers,
          body: payload,
          keepalive: keepalive,
          credentials: 'omit',
          mode: 'cors'
        })
        .then(function(response) {
          settle();
          if (!response.ok && attempt < retryAttempts) {
            setTimeout(function() {
              send(events, attempt + 1);
            }, Math.pow(2, attempt) * 1000);
          } else {
            isSending = false;
          }
        })
        .catch(function(error) {
          settle();
          console.warn('Verops RUM: Error sending events', error);
          if (attempt < retryAttempts) {
            setTimeout(function() {
              send(events, attempt + 1);
            }, Math.pow(2, attempt) * 1000);
          } else {
            isSending = false;
          }
        });
      } else {
        // Fallback to XHR for very old browsers (no fetch). withCredentials
        // stays false so cookies aren't sent — same privacy posture as fetch.
        var xhr = new XMLHttpRequest();
        xhr.open('POST', endpoint, true);
        xhr.withCredentials = false;
        xhr.setRequestHeader('Content-Type', 'text/plain');
        if (apiKey) {
          xhr.setRequestHeader('X-RUM-Key', apiKey);
        }

        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            isSending = false;
          } else if (attempt < retryAttempts) {
            setTimeout(function() {
              send(events, attempt + 1);
            }, Math.pow(2, attempt) * 1000);
          } else {
            isSending = false;
          }
        };

        xhr.onerror = function() {
          console.error('Verops RUM: Error sending events');
          if (attempt < retryAttempts) {
            setTimeout(function() {
              send(events, attempt + 1);
            }, Math.pow(2, attempt) * 1000);
          } else {
            isSending = false;
          }
        };

        xhr.send(payload);
      }
    }

    return {
      init: init,
      enqueue: enqueue,
      flush: flush,
      flushAll: flushAll,
      // Runs just before the unload flush, so late reports ride along with it.
      beforeFlushAll: function(fn) { beforeFlushAll.push(fn); }
    };
  })();

  // ============================================================================
  // WEB VITALS COLLECTOR
  // ============================================================================

  var WebVitalsCollector = (function() {
    var vitals = {};
    var observers = [];

    function observeLCP() {
      if (!window.PerformanceObserver) return;

      try {
        var observer = new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          var lastEntry = entries[entries.length - 1];
          vitals.lcp = {
            value: lastEntry.renderTime || lastEntry.loadTime,
            element: lastEntry.element ? getElementSelector(lastEntry.element) : null
          };
        });

        observer.observe({ type: 'largest-contentful-paint', buffered: true });
        observers.push(observer);
      } catch (e) {
        console.warn('Verops RUM: Error observing LCP', e);
      }
    }

    function observeFID() {
      if (!window.PerformanceObserver) return;

      try {
        var observer = new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          entries.forEach(function(entry) {
            if (entry.name === 'first-input' || entry.entryType === 'first-input') {
              vitals.fid = {
                value: entry.processingStart - entry.startTime,
                element: entry.target ? getElementSelector(entry.target) : null
              };
            }
          });
        });

        observer.observe({ type: 'first-input', buffered: true });
        observers.push(observer);
      } catch (e) {
        console.warn('Verops RUM: Error observing FID', e);
      }
    }

    function observeINP() {
      if (!window.PerformanceObserver) return;

      try {
        var observer = new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          var maxDuration = 0;
          entries.forEach(function(entry) {
            if (entry.duration > maxDuration) {
              maxDuration = entry.duration;
              vitals.inp = {
                value: entry.duration,
                element: entry.target ? getElementSelector(entry.target) : null
              };
            }
          });
        });

        observer.observe({ type: 'event', buffered: true, durationThreshold: 40 });
        observers.push(observer);
      } catch (e) {
        console.warn('Verops RUM: Error observing INP', e);
      }
    }

    function observeCLS() {
      if (!window.PerformanceObserver) return;

      try {
        var clsValue = 0;
        var clsSources = [];

        var observer = new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          entries.forEach(function(entry) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
              if (entry.sources && entry.sources.length > 0) {
                entry.sources.forEach(function(source) {
                  if (source.node) {
                    clsSources.push(getElementSelector(source.node));
                  }
                });
              }
            }
          });
          vitals.cls = {
            value: clsValue,
            sources: clsSources.slice(0, 5) // Keep top 5 sources
          };
        });

        observer.observe({ type: 'layout-shift', buffered: true });
        observers.push(observer);
      } catch (e) {
        console.warn('Verops RUM: Error observing CLS', e);
      }
    }

    function observeFCP() {
      if (!window.PerformanceObserver) {
        // Fallback for browsers without PerformanceObserver
        if (window.performance && window.performance.getEntriesByType) {
          var paintEntries = performance.getEntriesByType('paint');
          paintEntries.forEach(function(entry) {
            if (entry.name === 'first-contentful-paint') {
              vitals.fcp = {
                value: entry.startTime
              };
            }
          });
        }
        return;
      }

      try {
        var observer = new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          entries.forEach(function(entry) {
            if (entry.name === 'first-contentful-paint') {
              vitals.fcp = {
                value: entry.startTime
              };
            }
          });
        });

        observer.observe({ type: 'paint', buffered: true });
        observers.push(observer);
      } catch (e) {
        console.warn('Verops RUM: Error observing FCP', e);
        // Fallback to synchronous method
        if (window.performance && window.performance.getEntriesByType) {
          var paintEntries = performance.getEntriesByType('paint');
          paintEntries.forEach(function(entry) {
            if (entry.name === 'first-contentful-paint') {
              vitals.fcp = {
                value: entry.startTime
              };
            }
          });
        }
      }
    }

    function measureTTFB() {
      if (!window.performance || !window.performance.timing) return;

      var timing = performance.timing;
      vitals.ttfb = {
        value: timing.responseStart - timing.requestStart
      };
    }

    function measureTTI() {
      // Simplified TTI calculation
      if (!window.performance || !window.performance.timing) return;

      var timing = performance.timing;
      var tti = timing.domInteractive - timing.navigationStart;
      vitals.tti = {
        value: tti
      };
    }

    function getElementSelector(element) {
      if (!element || !element.tagName) return null;

      if (element.id) {
        return '#' + element.id;
      }

      if (element.className && typeof element.className === 'string') {
        var classes = element.className.trim().split(/\s+/).slice(0, 2).join('.');
        if (classes) {
          return element.tagName.toLowerCase() + '.' + classes;
        }
      }

      return element.tagName.toLowerCase();
    }

    function init() {
      // Start observing FCP immediately (it happens early in page load)
      // buffered: true will capture it even if it already occurred
      observeFCP();

      // Wait for page load for other vitals
      if (document.readyState === 'complete') {
        collectVitals();
      } else {
        window.addEventListener('load', collectVitals);
      }
    }

    function collectVitals() {
      // Start observing other vitals
      observeLCP();
      observeFID();
      observeINP();
      observeCLS();
      measureTTFB();

      // Measure TTI after a short delay
      setTimeout(measureTTI, 0);

      // Report once, when the page is hidden or unloaded, so the values are
      // final: an LCP that lands late, interactions after load (INP) and
      // later layout shifts all count. A fixed timer missed them and sent
      // nothing for pages left before it fired.
      BeaconSender.beforeFlushAll(reportVitals);
    }

    var reported = false;

    function reportVitals() {
      if (reported || Object.keys(vitals).length === 0) return;
      reported = true;

      VeropsRUM.trackEvent('web_vitals', {
        // All timing metrics in milliseconds for consistency with backend expectations
        // LCP: Keep in milliseconds
        lcp: vitals.lcp ? vitals.lcp.value : null,
        // FID: Keep in milliseconds
        fid: vitals.fid ? vitals.fid.value : null,
        // CLS: Unitless, keep as-is
        cls: vitals.cls ? vitals.cls.value : null,
        // FCP: Keep in milliseconds
        fcp: vitals.fcp ? vitals.fcp.value : null,
        // TTFB: Keep in milliseconds
        ttfb: vitals.ttfb ? vitals.ttfb.value : null,
        // TTI: Keep in milliseconds
        tti: vitals.tti ? vitals.tti.value : null,
        // INP: Keep in milliseconds
        inp: vitals.inp ? vitals.inp.value : null,
        details: vitals
      });
    }

    function disconnect() {
      observers.forEach(function(observer) {
        observer.disconnect();
      });
      observers = [];
    }

    return {
      init: init,
      disconnect: disconnect,
      getVitals: function() { return vitals; }
    };
  })();

  // ============================================================================
  // ERROR HANDLER
  // ============================================================================

  var ErrorHandler = (function() {
    var oldErrorHandler = null;
    var oldRejectionHandler = null;
    var oldConsoleError = null;
    var recentErrors = [];
    var MAX_RECENT = 20;
    var DEDUP_WINDOW_MS = 2000;

    function isDuplicate(key) {
      var now = Date.now();
      recentErrors = recentErrors.filter(function(entry) {
        return now - entry.time < DEDUP_WINDOW_MS;
      });
      for (var i = 0; i < recentErrors.length; i++) {
        if (recentErrors[i].key === key) return true;
      }
      return false;
    }

    function trackRecent(key) {
      recentErrors.push({ key: key, time: Date.now() });
      if (recentErrors.length > MAX_RECENT) {
        recentErrors.shift();
      }
    }

    function init() {
      // Capture JavaScript errors via window.onerror
      oldErrorHandler = window.onerror;
      window.onerror = function(message, source, lineno, colno, error) {
        captureError({
          type: 'javascript_error',
          message: message,
          source: source,
          lineno: lineno,
          colno: colno,
          stack: error ? error.stack : null,
          error: error
        });

        // Call original handler
        if (oldErrorHandler) {
          return oldErrorHandler.apply(this, arguments);
        }
        return false;
      };

      // Capture unhandled promise rejections
      oldRejectionHandler = window.onunhandledrejection;
      window.addEventListener('unhandledrejection', function(event) {
        captureError({
          type: 'unhandled_rejection',
          message: event.reason ? event.reason.message || String(event.reason) : 'Unhandled Promise Rejection',
          stack: event.reason ? event.reason.stack : null,
          reason: event.reason
        });

        // Call original handler
        if (oldRejectionHandler) {
          return oldRejectionHandler.call(this, event);
        }
      });

      // Capture resource errors (images, scripts, stylesheets that fail to load)
      window.addEventListener('error', function(event) {
        if (event.target !== window) {
          captureResourceError(event);
        }
      }, true);

      // Intercept console.error to capture framework-internal errors.
      // React catches rendering/lifecycle errors internally and logs them
      // via console.error() without throwing to window.onerror.
      oldConsoleError = console.error;
      console.error = function() {
        // Always call original first so dev tools still work
        oldConsoleError.apply(console, arguments);

        try {
          var args = Array.prototype.slice.call(arguments);
          if (args.length === 0) return;

          // Skip React's duplicate follow-up messages
          var firstArg = typeof args[0] === 'string' ? args[0] : '';
          if (firstArg.indexOf('The above error occurred in') !== -1) return;
          if (firstArg.indexOf('Consider adding an error boundary') !== -1) return;
          // Skip React hydration warnings and other dev-only noise
          if (firstArg.indexOf('Warning:') === 0) return;
          // Never report the SDK's own diagnostics as the site's errors
          if (firstArg.indexOf('Verops RUM:') === 0) return;

          // Find Error objects in arguments
          var errorObj = null;
          for (var i = 0; i < args.length; i++) {
            if (args[i] instanceof Error) {
              errorObj = args[i];
              break;
            }
          }

          // Build message string from all arguments
          var message = args.map(function(arg) {
            if (arg instanceof Error) return arg.message;
            if (typeof arg === 'string') return arg;
            try { return JSON.stringify(arg); } catch(e) { return String(arg); }
          }).join(' ');

          if (!message || message.length === 0) return;

          captureError({
            type: 'console_error',
            message: message.substring(0, 4000),
            stack: errorObj ? errorObj.stack : null,
            source: 'console.error'
          });
        } catch (e) {
          // Never break console.error under any circumstances
        }
      };
    }

    function captureError(errorData) {
      // Deduplicate: skip if same message was reported within the dedup window.
      // This prevents double-reporting when an error fires through both
      // window.onerror and console.error.
      var dedupKey = (errorData.message || '').substring(0, 200);
      if (isDuplicate(dedupKey)) return;
      trackRecent(dedupKey);

      VeropsRUM.trackEvent('error', {
        error_type: errorData.type,
        message: errorData.message,
        source: errorData.source,
        lineno: errorData.lineno,
        colno: errorData.colno,
        stack: errorData.stack,
        timestamp: Date.now()
      });
    }

    function captureResourceError(event) {
      var target = event.target || event.srcElement;
      var resourceType = target.tagName ? target.tagName.toLowerCase() : 'unknown';

      VeropsRUM.trackEvent('resource_error', {
        resource_type: resourceType,
        url: target.src || target.href,
        timestamp: Date.now()
      });
    }

    function disconnect() {
      if (oldErrorHandler) {
        window.onerror = oldErrorHandler;
      }
      if (oldRejectionHandler) {
        window.onunhandledrejection = oldRejectionHandler;
      }
      if (oldConsoleError) {
        console.error = oldConsoleError;
      }
    }

    return {
      init: init,
      disconnect: disconnect,
      captureError: captureError
    };
  })();

  // ============================================================================
  // PERFORMANCE MONITOR
  // ============================================================================

  var PerformanceMonitor = (function() {
    function init() {
      if (document.readyState === 'complete') {
        collectPerformanceData();
      } else {
        window.addEventListener('load', collectPerformanceData);
      }
    }

    function collectPerformanceData() {
      setTimeout(function() {
        collectNavigationTiming();
        collectResourceTiming();
      }, 0);
    }

    function collectNavigationTiming() {
      if (!window.performance || !window.performance.timing) return;

      var timing = performance.timing;
      var navigationStart = timing.navigationStart;

      var timingData = {
        dns: timing.domainLookupEnd - timing.domainLookupStart,
        tcp: timing.connectEnd - timing.connectStart,
        request: timing.responseStart - timing.requestStart,
        response: timing.responseEnd - timing.responseStart,
        dom_processing: timing.domComplete - timing.domLoading,
        dom_content_loaded: timing.domContentLoadedEventEnd - timing.domContentLoadedEventStart,
        load_event: timing.loadEventEnd - timing.loadEventStart,
        total: timing.loadEventEnd - navigationStart,
        redirect: timing.redirectEnd - timing.redirectStart,
        unload: timing.unloadEventEnd - timing.unloadEventStart
      };

      VeropsRUM.trackEvent('navigation_timing', timingData);
    }

    function collectResourceTiming() {
      if (!window.performance || !window.performance.getEntriesByType) return;

      var resources = performance.getEntriesByType('resource');
      var resourceSummary = {
        total: resources.length,
        by_type: {},
        slow_resources: [],
        // Enhanced metrics for breakdown
        total_size: 0,
        total_duration: 0,
        js_count: 0,
        js_size: 0,
        css_count: 0,
        css_size: 0,
        img_count: 0,
        img_size: 0,
        font_count: 0,
        font_size: 0
      };

      resources.forEach(function(resource) {
        var type = getResourceType(resource.initiatorType, resource.name);
        resourceSummary.by_type[type] = (resourceSummary.by_type[type] || 0) + 1;

        // Track total size and duration
        if (resource.transferSize) {
          resourceSummary.total_size += resource.transferSize;
        }
        if (resource.duration) {
          resourceSummary.total_duration += resource.duration;
        }

        // Track by resource type
        if (type === 'script') {
          resourceSummary.js_count++;
          if (resource.transferSize) resourceSummary.js_size += resource.transferSize;
        } else if (type === 'link' || type === 'css') {
          resourceSummary.css_count++;
          if (resource.transferSize) resourceSummary.css_size += resource.transferSize;
        } else if (type === 'img') {
          resourceSummary.img_count++;
          if (resource.transferSize) resourceSummary.img_size += resource.transferSize;
        } else if (type === 'font') {
          resourceSummary.font_count++;
          if (resource.transferSize) resourceSummary.font_size += resource.transferSize;
        }

        // Track slow resources (> 1 second)
        if (resource.duration > 1000) {
          resourceSummary.slow_resources.push({
            name: resource.name,
            type: type,
            duration: Math.round(resource.duration),
            size: resource.transferSize
          });
        }
      });

      // Limit slow resources to top 10
      resourceSummary.slow_resources = resourceSummary.slow_resources
        .sort(function(a, b) { return b.duration - a.duration; })
        .slice(0, 10);

      VeropsRUM.trackEvent('resource_timing', resourceSummary);
    }

    function getResourceType(initiatorType, name) {
      if (initiatorType) return initiatorType;

      var ext = name.split('.').pop().split('?')[0].toLowerCase();
      var typeMap = {
        'js': 'script',
        'css': 'link',
        'jpg': 'img',
        'jpeg': 'img',
        'png': 'img',
        'gif': 'img',
        'svg': 'img',
        'webp': 'img',
        'woff': 'font',
        'woff2': 'font',
        'ttf': 'font',
        'otf': 'font'
      };

      return typeMap[ext] || 'other';
    }

    return {
      init: init
    };
  })();

  // ============================================================================
  // USER INTERACTION TRACKER
  // ============================================================================

  var InteractionTracker = (function() {
    var clickHandler = null;
    var scrollHandler = null;
    var formHandlers = [];
    var maxScrollDepth = 0;
    var scrollCheckInterval = null;

    function init() {
      trackClicks();
      trackScrollDepth();
      trackFormSubmissions();
    }

    function trackClicks() {
      clickHandler = function(event) {
        var target = event.target;
        var elementInfo = getElementInfo(target);

        // Throttle click events
        if (shouldTrackClick(elementInfo)) {
          VeropsRUM.trackEvent('click', {
            element: elementInfo.selector,
            xpath: elementInfo.xpath,
            tag_name: elementInfo.tagName,
            element_id: elementInfo.id,
            element_class: elementInfo.className,
            text: elementInfo.text,
            href: elementInfo.href,
            x: event.clientX,
            y: event.clientY
          });
        }

        SessionManager.updateActivity();
      };

      document.addEventListener('click', clickHandler, true);
    }

    function shouldTrackClick(elementInfo) {
      // Track clicks on links, buttons, and interactive elements
      return elementInfo.tagName === 'a' ||
             elementInfo.tagName === 'button' ||
             elementInfo.role === 'button' ||
             elementInfo.role === 'link';
    }

    function trackScrollDepth() {
      scrollHandler = throttle(function() {
        var scrollDepth = Math.round(
          (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100
        );

        if (scrollDepth > maxScrollDepth) {
          maxScrollDepth = scrollDepth;

          // Track milestones: 25%, 50%, 75%, 100%
          if ([25, 50, 75, 100].indexOf(maxScrollDepth) !== -1) {
            VeropsRUM.trackEvent('scroll_depth', {
              depth: maxScrollDepth,
              pixel_depth: window.scrollY + window.innerHeight
            });
          }
        }

        SessionManager.updateActivity();
      }, 500);

      window.addEventListener('scroll', scrollHandler, { passive: true });
    }

    function trackFormSubmissions() {
      var forms = document.getElementsByTagName('form');

      for (var i = 0; i < forms.length; i++) {
        var form = forms[i];
        var submitHandler = createFormSubmitHandler(form);
        form.addEventListener('submit', submitHandler);
        formHandlers.push({ form: form, handler: submitHandler });
      }
    }

    function createFormSubmitHandler(form) {
      return function(event) {
        var formInfo = {
          id: form.id || null,
          name: form.name || null,
          action: form.action || null,
          method: form.method || 'get',
          field_count: form.elements.length
        };

        VeropsRUM.trackEvent('form_submit', formInfo);
        SessionManager.updateActivity();
      };
    }

    function getElementInfo(element) {
      return {
        tagName: element.tagName ? element.tagName.toLowerCase() : null,
        id: element.id || null,
        className: element.className || null,
        role: element.getAttribute('role') || null,
        text: getElementText(element),
        href: element.href || null,
        selector: getElementSelector(element),
        xpath: getElementXPath(element)
      };
    }

    function getElementXPath(element) {
      if (!element || element.nodeType !== 1) return null;

      // If element has an ID, use that as it's unique
      if (element.id) {
        return '//*[@id="' + element.id + '"]';
      }

      var parts = [];
      var current = element;

      while (current && current.nodeType === 1) {
        var index = 1;
        var sibling = current.previousSibling;

        // Count preceding siblings with same tag name
        while (sibling) {
          if (sibling.nodeType === 1 && sibling.tagName === current.tagName) {
            index++;
          }
          sibling = sibling.previousSibling;
        }

        var tagName = current.tagName ? current.tagName.toLowerCase() : 'unknown';
        var part = tagName;

        // Check if there are siblings with the same tag
        var hasSiblings = false;
        sibling = current.nextSibling;
        while (sibling) {
          if (sibling.nodeType === 1 && sibling.tagName === current.tagName) {
            hasSiblings = true;
            break;
          }
          sibling = sibling.nextSibling;
        }

        // Add index if there are multiple siblings or if we counted more than 1
        if (index > 1 || hasSiblings) {
          part += '[' + index + ']';
        }

        parts.unshift(part);
        current = current.parentNode;

        // Stop at body
        if (current && current.tagName && current.tagName.toLowerCase() === 'body') {
          parts.unshift('body');
          break;
        }
      }

      return '/' + parts.join('/');
    }

    function getElementText(element) {
      var text = element.textContent || element.innerText || '';
      return text.trim().substring(0, 100); // Limit to 100 chars
    }

    function getElementSelector(element) {
      if (!element || !element.tagName) return null;

      if (element.id) {
        return '#' + element.id;
      }

      if (element.className && typeof element.className === 'string') {
        var classes = element.className.trim().split(/\s+/).slice(0, 2).join('.');
        if (classes) {
          return element.tagName.toLowerCase() + '.' + classes;
        }
      }

      return element.tagName.toLowerCase();
    }

    function disconnect() {
      if (clickHandler) {
        document.removeEventListener('click', clickHandler, true);
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
      formHandlers.forEach(function(item) {
        item.form.removeEventListener('submit', item.handler);
      });
      formHandlers = [];
    }

    return {
      init: init,
      disconnect: disconnect
    };
  })();

  // ============================================================================
  // PAGE VIEW TRACKER
  // ============================================================================

  /**
   * Compute the "logical page path" the SDK should report.
   *
   * Rules:
   *   - Always start with location.pathname
   *   - Append search if present (so /products?cat=a vs /products?cat=b
   *     are tracked separately; many SPAs swap categories via search)
   *   - Append hash IFF it looks like a route — i.e. starts with "#/"
   *     This is the convention HashRouter, AngularJS, Vue Router (hash mode)
   *     all use. We deliberately ignore plain anchors like "#about" so we
   *     don't fragment a page on every in-page link.
   *
   * Used everywhere so the comparison, the trackEvent payload and the
   * envelope all see the same value.
   */
  function getLogicalPath() {
    var path = window.location.pathname || '/';
    if (window.location.search) {
      path += window.location.search;
    }
    var hash = window.location.hash;
    if (hash && hash.length > 1 && hash.charAt(1) === '/') {
      path += hash;
    }
    return path;
  }

  /**
   * Same idea but for a "logical full URL" — page_url. We don't strip
   * anything; just expose it through a helper so callers don't sprinkle
   * window.location.href everywhere.
   */
  function getLogicalUrl() {
    return window.location.href;
  }

  var PageViewTracker = (function() {
    var currentPath = null;
    var pageLoadTime = null;
    // Deferred-title timer — coalesces back-to-back route changes (e.g.
    // a redirect chain) into a single page_view with the final title.
    var titleDeferTimer = null;
    // SPA frameworks update document.title async after the route transition
    // finishes. 80ms is enough for React, Vue, Angular, Svelte; if the
    // framework is still slow, the title falls back to whatever was set
    // when the timer fired.
    var TITLE_DEFER_MS = 80;

    function init() {
      trackPageView();
      trackSPANavigation();
    }

    function trackPageView() {
      pageLoadTime = Date.now();
      currentPath = getLogicalPath();

      var pageData = {
        url: getLogicalUrl(),
        path: currentPath,
        title: document.title,
        referrer: document.referrer,
        viewport: getViewportSize(),
        screen: getScreenResolution()
      };

      VeropsRUM.trackEvent('page_view', pageData);
      SessionManager.incrementPageViews();
    }

    function trackSPANavigation() {
      // History API (modern SPAs) — pushState / replaceState / popstate.
      var originalPushState = history.pushState;
      var originalReplaceState = history.replaceState;

      history.pushState = function() {
        originalPushState.apply(this, arguments);
        handleRouteChange();
      };

      history.replaceState = function() {
        originalReplaceState.apply(this, arguments);
        handleRouteChange();
      };

      window.addEventListener('popstate', handleRouteChange);

      // Hash-only routers (legacy Angular, Vue Router hash mode, plain
      // anchor-based routing). These never fire pushState — only
      // hashchange. Without this listener every #/route navigation was
      // silently ignored.
      window.addEventListener('hashchange', handleRouteChange);
    }

    function handleRouteChange() {
      var newPath = getLogicalPath();
      if (newPath === currentPath) return;

      // Capture exit metrics for the page we're leaving
      var prevPath = currentPath;
      var prevLoadTime = pageLoadTime;
      currentPath = newPath;
      pageLoadTime = Date.now();

      if (prevLoadTime) {
        var timeOnPage = pageLoadTime - prevLoadTime;
        VeropsRUM.trackEvent('page_exit', {
          path: prevPath,
          time_on_page: timeOnPage
        });
      }

      // Defer the page_view event so the SPA framework has time to
      // update document.title. If a second navigation happens in the
      // meantime, we cancel and reschedule — the user only sees the
      // final destination, not the intermediate redirect.
      if (titleDeferTimer) clearTimeout(titleDeferTimer);
      var emitForPath = newPath;
      titleDeferTimer = setTimeout(function() {
        // Bail if another navigation has superseded this one.
        if (emitForPath !== currentPath) return;

        var pageData = {
          url: getLogicalUrl(),
          path: currentPath,
          title: document.title,
          navigation_type: 'spa'
        };

        VeropsRUM.trackEvent('page_view', pageData);
        SessionManager.incrementPageViews();
      }, TITLE_DEFER_MS);
    }

    return {
      init: init
    };
  })();

  // ============================================================================
  // MAIN SDK
  // ============================================================================

  // Consent state — module-scoped so the consent API is callable
  // BEFORE init() runs. Consent banners typically fire before the
  // RUM SDK has initialised; without this stub, operator code like
  // {@code window.VeropsRUM.consent.set('granted')} throws
  // "Cannot read properties of undefined (reading 'consent')" the
  // moment a cookie banner accept button is clicked.
  //
  // States: 'unknown' | 'granted' | 'denied'. When 'denied', the
  // beacon send-path silently drops events. Operators flip state
  // at any time; listeners registered via {@code onChange} fire on
  // transitions.
  var __veropsConsentState = 'unknown';
  var __veropsConsentListeners = [];
  var __veropsConsentApi = {
    set: function (state) {
      if (state !== 'granted' && state !== 'denied' && state !== 'unknown') {
        console.warn('VeropsRUM consent.set: unknown state', state, '— accepts granted/denied/unknown');
        return;
      }
      __veropsConsentState = state;
      try {
        for (var i = 0; i < __veropsConsentListeners.length; i++) __veropsConsentListeners[i](state);
      } catch (_) { /* listener exceptions must not break consent flow */ }
    },
    get: function () { return __veropsConsentState; },
    state: function () { return __veropsConsentState; },
    granted: function () { __veropsConsentApi.set('granted'); },
    denied: function () { __veropsConsentApi.set('denied'); },
    // Back-compat aliases — operators call them in whichever spelling
    // their CMP / banner library taught them. Mirror all the common
    // names so the SDK doesn't blow up on a typo.
    grant: function () { __veropsConsentApi.set('granted'); },
    deny: function () { __veropsConsentApi.set('denied'); },
    accept: function () { __veropsConsentApi.set('granted'); },
    reject: function () { __veropsConsentApi.set('denied'); },
    onChange: function (fn) {
      if (typeof fn === 'function') __veropsConsentListeners.push(fn);
    },
    /** True when consent is anything except explicitly 'denied'. */
    isAllowed: function () { return __veropsConsentState !== 'denied'; }
  };

  var VeropsRUM = {
    version: '2.3.3',
    initialized: false,
    /** Consent API — exposed BEFORE init() so consent banners can
     *  flip state any time without checking init order. */
    consent: __veropsConsentApi,
    config: {
      appId: null,
      apiKey: null,
      endpoint: null,
      environment: 'production',
      sampleRate: 1.0,
      sessionSampleRate: 1.0,
      sessionReplaySampleRate: 0.0,
      maskAllInputs: true,
      maskAllText: false,
      trackPageViews: true,
      trackErrors: true,
      trackWebVitals: true,
      trackUserInteractions: true,
      recordSession: false,
      user: null,
      customProperties: {},

      // Enhanced features (opt-in for backward compatibility)
      enableSmartActions: false,
      enableFrustrationDetect: false,
      enableFormTracking: false
    },
    sampled: true,

    /**
     * Initialize the SDK
     */
    init: function(config) {
      if (this.initialized) {
        console.warn('Verops RUM: Already initialized');
        return;
      }

      // Merge config
      this.config = mergeObjects(this.config, config || {});

      // Validate required config
      if (!this.config.appId || !this.config.apiKey || !this.config.endpoint) {
        console.error('Verops RUM: Missing required configuration (appId, apiKey, endpoint)');
        return;
      }

      // Check sampling
      this.sampled = shouldSample(this.config.sampleRate);
      if (!this.sampled) {
        console.log('Verops RUM: Session not sampled');
        return;
      }

      // Initialize beacon sender
      BeaconSender.init(this.config.endpoint, this.config.apiKey);

      // Track initial page load
      if (this.config.trackPageViews) {
        PageViewTracker.init();
      }

      // Initialize error tracking
      if (this.config.trackErrors) {
        ErrorHandler.init();
      }

      // Initialize web vitals collection
      if (this.config.trackWebVitals) {
        WebVitalsCollector.init();
      }

      // Initialize user interaction tracking
      if (this.config.trackUserInteractions) {
        InteractionTracker.init();
      }

      // Initialize performance monitoring
      PerformanceMonitor.init();

      // Initialize smart features (NEW)
      if (this.config.enableSmartActions) {
        var self = this;
        document.addEventListener('click', function(event) {
          var classification = SmartActions.classify(event.target, event);
          if (classification.confidence >= 0.70) {
            self.trackEvent('smart_action', {
              action_type: classification.actionType,
              confidence: classification.confidence,
              element: classification.element,
              text: classification.text,
              page: window.location.pathname
            });
          }
        }, true);
        console.log('Verops RUM: Smart Actions enabled');
      }

      if (this.config.enableFrustrationDetect) {
        FrustrationDetector.init();
        console.log('Verops RUM: Frustration Detection enabled');
      }

      if (this.config.enableFormTracking) {
        // Wait for DOM to be ready
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
          FormTracker.init();
        } else {
          window.addEventListener('DOMContentLoaded', function() {
            FormTracker.init();
          });
        }
        console.log('Verops RUM: Form Tracking enabled');
      }

      // Track SDK initialization
      this.trackEvent('rum_init', {
        version: this.version,
        config: {
          environment: this.config.environment,
          trackPageViews: this.config.trackPageViews,
          trackErrors: this.config.trackErrors,
          trackWebVitals: this.config.trackWebVitals,
          trackUserInteractions: this.config.trackUserInteractions,
          enableSmartActions: this.config.enableSmartActions,
          enableFrustrationDetect: this.config.enableFrustrationDetect,
          enableFormTracking: this.config.enableFormTracking
        }
      });

      this.initialized = true;
      console.log('Verops RUM: Initialized v' + this.version);
    },

    /**
     * Track a custom event
     */
    trackEvent: function(eventName, properties) {
      if (!this.sampled) return;

      // Consent gate: explicit 'denied' silently drops everything.
      // 'unknown' and 'granted' both let events through — we can't
      // delay startup beacons indefinitely waiting for a banner.
      if (!__veropsConsentApi.isAllowed()) return;

      properties = properties || {};

      // Filter SDK internal network events
      if (properties.url && shouldFilterSDKEvent(properties.url)) {
        return; // Don't track SDK's own beacon calls
      }

      // Get screen resolution
      var screenInfo = getScreenResolution();

      // Pre-parse the UA so the backend doesn't have to do it on every
      // aggregation read. These end up as dedicated columns
      // (rum_events.browser / .os / .device_type) and as fields on the
      // session row.
      var fp = detectClientFingerprint();

      var event = {
        event_name: eventName,
        event_type: eventName, // Also add as event_type for consistency
        timestamp: Date.now(),
        session_id: SessionManager.getSessionId(),
        app_id: this.config.appId,
        environment: this.config.environment,
        // Page URL/path captured via the same helper PageViewTracker
        // uses, so every event (click, error, web-vital) is correlated
        // to the same logical page — including SPA hash routes.
        page_url: getLogicalUrl(),
        page_path: getLogicalPath(),
        page_title: document.title,
        user_agent: getUserAgent(),
        browser: fp.browser,
        os: fp.os,
        device_type: fp.device_type,
        screen_width: screenInfo.width,
        screen_height: screenInfo.height,
        viewport_width: window.innerWidth || document.documentElement.clientWidth,
        viewport_height: window.innerHeight || document.documentElement.clientHeight,
        properties: properties
      };

      // Add UTM parameters if present
      var utmParams = getUTMParameters();
      if (utmParams) {
        event.utm_parameters = utmParams;
      }

      // Add user data if available
      if (this.config.user) {
        event.user = this.config.user;
      }

      // Add custom properties
      if (Object.keys(this.config.customProperties).length > 0) {
        event.custom_properties = this.config.customProperties;
      }

      // Add API key
      event.api_key = this.config.apiKey;

      BeaconSender.enqueue(event);
      SessionManager.updateActivity();
    },

    /**
     * Track a custom event (simplified API)
     */
    track: function(eventName, properties) {
      return this.trackEvent(eventName, properties);
    },

    /**
     * Track a custom metric
     */
    metric: function(metricName, value, unit) {
      return this.trackEvent('custom_metric', {
        metric_name: metricName,
        value: value,
        unit: unit || 'count'
      });
    },

    /**
     * Track an error
     */
    trackError: function(error, context) {
      if (!this.sampled) return;

      var errorData = {
        message: error.message || error,
        stack: error.stack || null,
        context: context || {}
      };

      ErrorHandler.captureError({
        type: 'manual_error',
        message: errorData.message,
        stack: errorData.stack,
        context: errorData.context
      });
    },

    /**
     * Set user information
     */
    setUser: function(user) {
      // Accept a bare id as shorthand for { id: ... }; ingest reads user.id.
      this.config.user = (typeof user === 'string' || typeof user === 'number') ? { id: String(user) } : user;
    },

    /**
     * Set custom properties
     */
    setCustomProperties: function(properties) {
      this.config.customProperties = mergeObjects(this.config.customProperties, properties);
    },

    /**
     * Get current session ID
     */
    getSessionId: function() {
      return SessionManager.getSessionId();
    },

    /**
     * End current session
     */
    endSession: function() {
      BeaconSender.flush();
      SessionManager.endSession();
    },

    /**
     * Manually flush events
     */
    flush: function() {
      BeaconSender.flush();
    },

    /**
     * Disconnect and cleanup
     */
    disconnect: function() {
      if (!this.initialized) return;

      BeaconSender.flush();

      if (this.config.trackErrors) {
        ErrorHandler.disconnect();
      }

      if (this.config.trackWebVitals) {
        WebVitalsCollector.disconnect();
      }

      if (this.config.trackUserInteractions) {
        InteractionTracker.disconnect();
      }

      this.initialized = false;
      console.log('Verops RUM: Disconnected');
    }
  };

  // Expose to global scope
  window.VeropsRUM = VeropsRUM;

  // Auto-initialize if config is provided via data attributes
  if (document.currentScript) {
    var script = document.currentScript;
    var appId = script.getAttribute('data-app-id');
    var apiKey = script.getAttribute('data-api-key');
    var endpoint = script.getAttribute('data-endpoint');

    if (appId && apiKey && endpoint) {
      // Wait for DOM ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          VeropsRUM.init({
            appId: appId,
            apiKey: apiKey,
            endpoint: endpoint
          });
        });
      } else {
        VeropsRUM.init({
          appId: appId,
          apiKey: apiKey,
          endpoint: endpoint
        });
      }
    }
  }

})(window);
